import { ClipboardEvent, FormEvent, KeyboardEvent, useMemo, useRef, useState } from "react";
import Logo from "../../components/Logo";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { usePatientAuth } from "../../store/authPatientStore";

const PENDING_OTP_REQUEST_ID_KEY = "agbaza.patient.login.otpRequestId";
const PENDING_USER_HINT_KEY = "agbaza.patient.login.userHint";
const nipRegex = /^\d{10}$/;

export default function PatientLogin() {
  const [nip, setNip] = useState("");
  const nipPartRefs = useRef<Array<HTMLInputElement | null>>([]);
  const nipParts = useMemo(
    () => [nip.slice(0, 2), nip.slice(2, 4), nip.slice(4, 6), nip.slice(6, 8), nip.slice(8, 10)],
    [nip]
  );
  const nipValid = useMemo(() => nipRegex.test(nip), [nip]);

  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = usePatientAuth();

  const updateNipPart = (index: number, rawValue: string) => {
    const digits = rawValue.replace(/\D/g, "").slice(0, 2);
    const parts = [...nipParts];
    parts[index] = digits;
    setNip(parts.join("").slice(0, 10));
    if (digits.length === 2 && index < 4) {
      requestAnimationFrame(() => nipPartRefs.current[index + 1]?.focus());
    }
  };

  const handleNipPartKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    if (event.key === "Backspace" && input.value.length === 0 && index > 0) {
      requestAnimationFrame(() => {
        const prev = nipPartRefs.current[index - 1];
        if (!prev) return;
        prev.focus();
        try {
          prev.setSelectionRange(prev.value.length, prev.value.length);
        } catch {
          /* ignore */
        }
      });
    }
  };

  const handleNipPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 10);
    if (!pasted) return;
    event.preventDefault();
    setNip(pasted);
    const nextIndex = Math.min(4, Math.floor((pasted.length - 1) / 2));
    requestAnimationFrame(() => nipPartRefs.current[nextIndex]?.focus());
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    const cleanedNip = nip.trim();
    if (!nipRegex.test(cleanedNip)) {
      setError("Le NIP doit contenir exactement 10 chiffres.");
      return;
    }

    setSubmitting(true);
    const result = await signIn({ nip: cleanedNip });
    setSubmitting(false);

    if (!result.ok) {
      setError(result.message || "Connexion impossible.");
      return;
    }

    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect");
    sessionStorage.setItem(PENDING_OTP_REQUEST_ID_KEY, result.otpRequestId);
    if (result.userHint) {
      sessionStorage.setItem(PENDING_USER_HINT_KEY, result.userHint);
    } else {
      sessionStorage.removeItem(PENDING_USER_HINT_KEY);
    }

    navigate("/patient/connexion/code", {
      replace: true,
      state: {
        otpRequestId: result.otpRequestId,
        userHint: result.userHint,
        redirect: redirect ?? undefined,
      },
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-[#0B1F16]">
      <main className="flex-1 w-full max-w-sm mx-auto flex flex-col items-center justify-center py-8">
        <div className="text-center">
          <Logo size={90} />
          <p className="mt-4 text-lg font-semibold text-[#0B1F16]">Bon retour sur AGBAZA</p>
          <p className="mt-1 text-sm text-gray-600">Connectez-vous avec votre NIP pour accéder à votre espace patient.</p>
        </div>

        <form onSubmit={onSubmit} className="mt-8 w-full space-y-3 px-4">
          <div>
            <label className="text-sm font-medium text-gray-600">NIP patient (10 chiffres)</label>
            <p className="mt-1 text-xs text-gray-500">
              Saisie par groupes de 2 chiffres ; vous pouvez aussi coller un NIP à 10 chiffres.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {nipParts.map((part, index) => (
                <span key={index} className="flex items-center gap-2">
                  <input
                    ref={(el) => {
                      nipPartRefs.current[index] = el;
                    }}
                    value={part}
                    onChange={(e) => updateNipPart(index, e.target.value)}
                    onKeyDown={(e) => handleNipPartKeyDown(index, e)}
                    onPaste={index === 0 ? handleNipPaste : undefined}
                    inputMode="numeric"
                    maxLength={2}
                    autoComplete="off"
                    disabled={submitting}
                    className="h-12 w-12 text-center rounded-[10px] border border-[#E5E7EB] bg-white px-1 text-[#0B1F16] outline-none focus:ring-2 focus:ring-[#00553E]/30"
                    style={{
                      borderColor: nipValid || nip === "" ? "#E5E7EB" : "#F87171",
                    }}
                  />
                  {index < 4 && <span className="text-gray-400 select-none">-</span>}
                </span>
              ))}
            </div>
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">{error}</div>
          )}
          <button
            type="submit"
            disabled={submitting || !nipValid}
            className="w-full h-12 rounded-[10px] font-semibold text-white cursor-pointer disabled:opacity-60"
            style={{ background: "#00553E" }}
          >
            {submitting ? "Envoi du code..." : "Se connecter"}
          </button>
          <div className="text-sm text-[#0B1F16]">
            Vous n&apos;avez pas encore de compte ?{" "}
            <Link className="font-semibold" style={{ color: "#00553E" }} to="/patient/inscription">
              Inscrivez-vous
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
